import React, { useEffect, useState, useRef } from 'react';
import Card from 'ui/components/atoms/Card';
import Tag from 'ui/components/atoms/Tag';
import CardNews from 'ui/components/molecules/CardNews';
import { capitalizeText, dateFormatter } from '../../../helpers/utils';
import eventClickNews from 'ui/lib/gtm/eventClickNews';
import pageViewNews from 'ui/lib/gtm/pageViewNews';
import { sendDataInChunks } from 'ui/lib/collectData';
import Link from 'next/link';
import useElementVisibility from '../../../hooks/useElementVisibility';
import useIsElementVisible from 'ui/hooks/useIsElementVisible';
import dynamic from 'next/dynamic';
import { useSelector } from 'react-redux';
import { IRootState } from '../../../store';
import { useRouter } from '../../../hooks/useCustomRouter';
import Head from 'next/head';
import Image from 'next/image';
import convertImageBase64 from '../../../helpers/utils/convertImage';
import eventClickProductKnowledgeList from '../../../helpers/emarsys/product-knowledge/eventClickProductKnowledgeList';

const ScrollDownButton = dynamic(() => import('ui/components/atoms/ScrollDownButton'), {
  ssr: true,
});

export interface IArticle {
  id?: string | number;
  titlePage?: string;
  slug?: string;
  slugUnique?: string;
  publishedDate?: string;
  heroImageLink?: string;
  heroLinkResizedImage?: string;
  altImage?: string;
  contentCategory?: {
    name: string;
  };
  articleType?: string;
  name?: string;
  creative?: string;
  position?: string;
  category?: string;
  bannerLink?: string;
  status?: string;
}

interface ArticleListProps {
  handleCollectDataLayer: (data: IArticle) => void;
  articles: IArticle[];
  showTag: boolean;
  newsHero: IArticle;
  valueMarginRoot: string;
  feature?: string;
  emarsysData?: {
    pageTitle: string;
    category: string;
    page_number: number;
  };
}

const ArticleList = ({
  newsHero,
  valueMarginRoot,
  articles = [],
  showTag = true,
  handleCollectDataLayer = () => {},
  feature = '',
  emarsysData = {
    pageTitle: '',
    category: '',
    page_number: 0,
  },
}: ArticleListProps) => {
  const router = useRouter();
  const [collectDataLayer, setCollectDataLayer] = useState([]);
  const typingTimeout = useRef(null);
  const cardNewsRef = useRef(null);
  const isNewsCardVisible = useElementVisibility(cardNewsRef, 0.125);
  let newsHeroSlug = '';
  if (newsHero) {
    const newsHeroSlugUnique = newsHero.slugUnique.split('/');
    newsHeroSlug = newsHeroSlugUnique[newsHeroSlugUnique.length - 1] || '';
  }

  const { isMobileScreen } = useSelector((state: IRootState) => state.page);
  const [isVisible, setIsVisible] = useState(true);

  const handleScroll = () => {
    const scrolled = document.documentElement.scrollTop;

    // set the button visibility based on the scroll position
    setIsVisible(scrolled < window.innerHeight - 50);
  };

  const scrollDown = () => {
    window.scrollTo({
      top: 700,
      behavior: 'smooth',
    });
  };

  useEffect(() => {
    // add event listener to detect scroll
    window.addEventListener('scroll', handleScroll);

    // remove event listener when component unmount
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  useEffect(() => {
    clearTimeout(typingTimeout.current);
    typingTimeout.current = setTimeout(() => {
      if (collectDataLayer?.length > 0) {
        const sorted = collectDataLayer.sort((a, b) => {
          const positionA = parseInt(a.position.split(' ')[0]);
          const positionB = parseInt(b.position.split(' ')[0]);
          return positionA - positionB;
        });
        sendDataInChunks(
          sorted,
          3,
          (chunk) => {
            pageViewNews(chunk);
          },
          setCollectDataLayer,
          ''
        );
      }
    }, 100);
  });

  const bannerRef = useIsElementVisible(
    () =>
      handleCollectDataLayer({
        id: newsHero?.id,
        name: newsHero?.titlePage?.toLowerCase() || 'Tidak Tersedia',
        creative: `${router.basePath}/${newsHeroSlug?.toLowerCase()}`,
        position: `1 - ${
          router.basePath === '/berita-dan-tips'
            ? 'berita & tips'
            : router.basePath?.slice(1)?.replace(/-/g, ' ')
        } ${newsHero?.contentCategory?.name?.toLowerCase()?.replace(/-/g, ' ')}`,
        category: (router?.query?.kategori as string)?.toLowerCase()?.replace(/-/g, ' ')
          ? (router?.query?.kategori as string)?.toLowerCase()?.replace(/-/g, ' ')
          : 'semua',
      }),
    [newsHero]
  );

  const category = router?.query?.kategori || '';
  return (
    <React.Fragment>
      <Head>
        <link rel="preload" as="image" href={newsHero?.heroImageLink} imageSizes="50vw" />
      </Head>
      {!isNewsCardVisible && (
        <ScrollDownButton className={isVisible ? 'block' : 'hidden'} onClick={scrollDown} />
      )}
      {(newsHero?.heroImageLink || newsHero?.heroLinkResizedImage) && (
        <Card
          ref={bannerRef}
          className="bg-reliableBlack3 border-b-[0.5px] border-reliableBlack10 md:col-span-2 lg:col-span-3"
        >
          <Link
            id="hero-banner-news"
            href={newsHero?.slug}
            onClick={() => {
              eventClickNews({
                id: newsHero?.id,
                title: newsHero?.titlePage,
                category: newsHero?.contentCategory?.name,
                slug: newsHeroSlug || '',
                index: 1,
                anotherCategory: (router?.query?.kategori as string)
                  ?.toLowerCase()
                  ?.replace(/-/g, ' ')
                  ? (router?.query?.kategori as string)?.toLowerCase()?.replace(/-/g, ' ')
                  : 'semua',
                baseSlug: router.basePath,
              });
              if (feature === 'LISTING_PRODUCT_KNOWLEDGE') {
                eventClickProductKnowledgeList(
                  emarsysData?.pageTitle,
                  emarsysData?.category,
                  emarsysData?.page_number,
                  'list review',
                  'lihat detail',
                  newsHero?.titlePage
                );
              }
            }}
          >
            {isMobileScreen ? (
              <Image
                src={newsHero?.heroImageLink || newsHero?.heroLinkResizedImage}
                alt={newsHero?.altImage}
                width={isMobileScreen ? 360 : 1240}
                height={isMobileScreen ? 202 : 698}
                priority={true}
                sizes="(min-width: 1240px) 1240px, (min-width: 1060px) 992px, calc(93.51vw + 19px)"
                quality={75}
                placeholder="blur"
                blurDataURL={`data:image/svg+xml;base64,${convertImageBase64(1240, 698)}`}
                className="w-full object-cover max-[768px]:aspect-video object-center min-[768px]:h-[450px]"
              />
            ) : (
              <Image
                src={newsHero?.heroLinkResizedImage || newsHero?.heroImageLink}
                alt={newsHero?.altImage}
                width={isMobileScreen ? 360 : 1240}
                height={isMobileScreen ? 202 : 698}
                priority={true}
                sizes="(min-width: 1240px) 1240px, (min-width: 1060px) 992px, calc(93.51vw + 19px)"
                quality={75}
                placeholder="blur"
                blurDataURL={`data:image/svg+xml;base64,${convertImageBase64(1240, 698)}`}
                className="w-full object-cover max-[768px]:aspect-video object-center min-[768px]:h-[450px]"
              />
            )}
            <div
              className={`${!newsHero?.contentCategory?.name && 'bg-reliableBlack3 capitalize'}`}
            >
              {category === '' && showTag ? (
                <Tag
                  className="rounded-br h-[23px] md:h-[28px] text-xs"
                  severity={
                    newsHero?.contentCategory?.name
                      ? newsHero?.contentCategory?.name.toLowerCase() === ''
                        ? 'info'
                        : 'warn'
                      : 'warn'
                  }
                >
                  {capitalizeText(newsHero?.contentCategory?.name)}
                </Tag>
              ) : null}
            </div>

            <div className="px-4 pt-2 pb-4 lg:pt-8 lg:px-6 lg:pb-6">
              <p className="text-sm font-semibold text-reliableBlack line-clamp-3 mb-1 lg:text-[28px] md:leading-[48px] lg:font-bold">
                {newsHero.titlePage}
              </p>
              <p className="text-xs font-normal text-reliableBlack80 lg:mb-4 lg:text-sm lg:text-reliableBlack">
                {newsHero.publishedDate ? dateFormatter(newsHero.publishedDate) : null}
              </p>
            </div>
          </Link>
        </Card>
      )}

      {articles.map((article, index) => {
        const slugUniqueSplitted = article.slugUnique.split('/');
        const slug = slugUniqueSplitted[slugUniqueSplitted.length - 1] || '';
        return (
          <div ref={index == 1 ? cardNewsRef : null} key={article.id}>
            <CardNews
              key={index}
              title={article.titlePage}
              slug={slug}
              href={article.slug}
              category={category}
              date={article.publishedDate ? dateFormatter(article.publishedDate) : null}
              image={article?.heroImageLink}
              altImage={article?.altImage}
              tag={article?.contentCategory && showTag ? article?.contentCategory?.name : ''}
              AdditionalClassName={`${showTag ? 'lg:-mt-8' : 'lg:-mt-0'}`}
              idArticle={article?.id}
              categoryArticle={article?.contentCategory?.name}
              indexArticle={index}
              handleCollectDataLayer={(data) => {
                setCollectDataLayer((prev) => [...prev, data]);
              }}
              rootMargin={isMobileScreen ? valueMarginRoot : null}
              feature={feature}
              emarsysData={{
                pageTitle: emarsysData?.pageTitle,
                category: emarsysData?.category,
                page_number: emarsysData?.page_number,
              }}
            />
          </div>
        );
      })}
    </React.Fragment>
  );
};

export default ArticleList;
